import {type QueryClient, skipToken, useQuery} from '@tanstack/react-query'
import {addDays} from 'date-fns'
import {useMemo} from 'react'

import {businessCalendarDate} from 'src/api/business-time'

import type {DispatchTimelineData, UnassignedJob} from '../dispatch-timeline/types'
import {parseApiDate, toApiDateString} from '../jobs/job-schedule-format'
import {getDispatchBoard} from './api'
import {mapBoardResponseToDispatchTimelineData} from './board-adapter'
import {dispatchKeys} from './query-keys'

const MAX_AVAILABILITY_DAYS = 31
const OPEN_ENDED_AVAILABILITY_DAYS = 14
const AVAILABILITY_STALE_MS = 60_000

type DayWindow = {startDate: string; endDate: string}

export function jobAvailabilityWindow(
    schedule: UnassignedJob['schedule'],
    today: Date = businessCalendarDate(),
): DayWindow | null {
    if (!schedule) {
        return null
    }

    const todayString = toApiDateString(today)
    const startDate = schedule.startDate < todayString ? todayString : schedule.startDate
    if (schedule.endDate != null && schedule.endDate < startDate) {
        return null
    }

    const days = schedule.endDate == null ? OPEN_ENDED_AVAILABILITY_DAYS : MAX_AVAILABILITY_DAYS
    const cap = toApiDateString(addDays(parseApiDate(startDate), days - 1))
    const endDate = schedule.endDate == null || schedule.endDate > cap ? cap : schedule.endDate
    return {startDate, endDate}
}

function boardCoversWindow(data: DispatchTimelineData, dayWindow: DayWindow): boolean {
    return (
        data.scheduleWindowStart != null &&
        data.scheduleWindowEnd != null &&
        data.scheduleWindowStart <= dayWindow.startDate &&
        data.scheduleWindowEnd >= dayWindow.endDate
    )
}

/** Merge unavailability and assignments from `extra` into `base`, keeping `base` entries on overlap. */
export function mergeAvailabilityBoard(
    base: DispatchTimelineData,
    extra: DispatchTimelineData,
): DispatchTimelineData {
    const extraResources = new Map(extra.resources.map((resource) => [resource.id, resource]))
    const resources = base.resources.map((resource) => {
        const more = extraResources.get(resource.id)?.unavailable ?? []
        if (more.length === 0) {
            return resource
        }

        const own = resource.unavailable ?? []
        const seen = new Set(own.map((entry) => `${entry.from}|${entry.until}|${entry.label}`))
        const added = more.filter(
            (entry) => !seen.has(`${entry.from}|${entry.until}|${entry.label}`),
        )
        return added.length > 0 ? {...resource, unavailable: [...own, ...added]} : resource
    })

    const knownJobIds = new Set([
        ...base.unassignedJobs.map((job) => job.id),
        ...base.assignments.map((assignment) => assignment.sourceJobId ?? assignment.id),
    ])
    const unassignedJobs = [
        ...base.unassignedJobs,
        ...extra.unassignedJobs.filter((job) => !knownJobIds.has(job.id)),
    ]
    const assignments = [
        ...base.assignments,
        ...extra.assignments.filter(
            (assignment) => !knownJobIds.has(assignment.sourceJobId ?? assignment.id),
        ),
    ]

    return {...base, resources, unassignedJobs, assignments}
}

export function invalidateAvailabilityBoards(queryClient: QueryClient) {
    void queryClient.invalidateQueries({queryKey: dispatchKeys.availabilityBoard()})
}

export function useJobAvailabilityData(
    job: UnassignedJob,
    data: DispatchTimelineData,
    enabled: boolean,
): DispatchTimelineData {
    const availabilityWindow = useMemo(() => jobAvailabilityWindow(job.schedule), [job.schedule])
    const neededWindow =
        enabled &&
        availabilityWindow != null &&
        data.scheduleWindowStart != null &&
        !boardCoversWindow(data, availabilityWindow)
            ? availabilityWindow
            : null
    const needed = neededWindow != null

    const query = useQuery({
        queryKey: dispatchKeys.availabilityBoardWindow(
            availabilityWindow?.startDate ?? '',
            availabilityWindow?.endDate ?? '',
        ),
        queryFn: neededWindow
            ? async () =>
                  mapBoardResponseToDispatchTimelineData(await getDispatchBoard(neededWindow))
            : skipToken,
        staleTime: AVAILABILITY_STALE_MS,
    })

    return useMemo(
        () => (needed && query.data ? mergeAvailabilityBoard(data, query.data) : data),
        [data, needed, query.data],
    )
}
